import type { IndexedCue } from '~shared/utils'

import type { Snapshot } from 'valtio'

/**
 * Where a cue can be dropped, relative to the cue owning the dropzone.
 *
 * `CUE` and `GROUP` group the two cues together, `BEFORE` and `AFTER` reorder.
 */
export enum CueDropzone {
  GROUP = 'group',
  CUE = 'cue',
  BEFORE = 'before',
  AFTER = 'after',
}

/**
 * Builds the dnd-kit droppable id for a zone, as `<zone>-<cueId>`.
 *
 * dnd-kit identifies droppables by a single string, so the zone and the cue it
 * belongs to are encoded into one and read back with {@link getCueDropzone} and
 * {@link getTargetCueId}.
 */
export const DroppableId = {
  [CueDropzone.GROUP]: (cueId: string) => `${CueDropzone.GROUP}-${cueId}`,
  [CueDropzone.CUE]: (cueId: string) => `${CueDropzone.CUE}-${cueId}`,
  [CueDropzone.BEFORE]: (cueId: string) => `${CueDropzone.BEFORE}-${cueId}`,
  [CueDropzone.AFTER]: (cueId: string) => `${CueDropzone.AFTER}-${cueId}`,
}

export const getCueDropzone = (
  droppableId: string,
): CueDropzone | undefined => {
  const [type] = droppableId.split('-')

  if (!Object.values(CueDropzone).includes(type as CueDropzone)) return

  return type as CueDropzone
}

export const getTargetCueId = (droppableId: string): string | undefined => {
  const [, cueId] = droppableId.split('-', 2)

  return cueId || undefined
}

/**
 * The cue a dropzone belongs to, as the drop rules see it.
 *
 * Identity plus the group holding it, which is all the rules need.
 */
export type DropTarget = {
  id: string
  parentId?: string | null
}

/**
 * Whether dropping the dragged cue on this zone is allowed.
 *
 * Pure, so the rules can be read and tested in one place instead of being
 * spread across four dropzone hooks.
 *
 * @remarks
 *   A null `draggingCue` leaves every zone enabled except the grouping zones on
 *   a cue that already sits in a group. `disabled` only matters once a drag is
 *   under way, and dnd-kit measures dropzones at drag start, so zones are not
 *   disabled pre-emptively while the list is idle.
 *
 * @param dropzone Which zone is asking.
 * @param draggingCue The cue being dragged, frozen at drag start.
 * @param target The cue owning the zone.
 */
export function isDropDisabled(
  dropzone: CueDropzone,
  draggingCue: Snapshot<IndexedCue> | null,
  target: DropTarget,
): boolean {
  // A cue cannot be dropped onto itself.
  if (draggingCue?.id === target.id) return true

  // Only groups carry children, so this is "is the dragged cue a group".
  const isDraggingGroup = Array.isArray(draggingCue?.children)

  switch (dropzone) {
    // Grouping. Groups cannot nest, and a cue already in a group is reordered
    // within it rather than regrouped.
    case CueDropzone.CUE:
    case CueDropzone.GROUP:
      return isDraggingGroup || !!target.parentId || !!draggingCue?.parentId

    // Insert above the target.
    case CueDropzone.BEFORE:
      return isDraggingGroup && !!target.parentId

    // Insert below the target. Only rendered on the last cue of a group, so a
    // group landing here would end up nested.
    case CueDropzone.AFTER:
      return isDraggingGroup
  }
}
